import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MarketView from './MarketView';
import type { MarketViewProps } from './MarketView';
import QualQuantOverlay from './QualQuantOverlay';
import type { SegmentInsight } from '../api/client';

export type CharacteristicsSection = 'kpis' | 'hcp_characteristics' | 'treatment_trends';

const SECTION_LABELS: Record<CharacteristicsSection, string> = {
  kpis: 'Key Metrics (KPIs)',
  hcp_characteristics: 'HCP Characteristics',
  treatment_trends: 'Treatment Trends',
};

interface SegmentMarketViewData {
  market_kpis?: Record<string, unknown>;
  hcp_characteristics?: Record<string, unknown>;
  treatment_trends?: Record<string, unknown>;
}

interface SegmentMarketViewPanelProps {
  data: SegmentMarketViewData | undefined | null;
  segmentName?: string;
  /** Sections hidden by the user. Persisted in segment metadata. */
  hiddenSections?: CharacteristicsSection[];
  /** When true, shows the edit pencil. Controlled by parent. */
  editMode?: boolean;
  /** When provided, enables hide/show editing. Called with updated hidden sections. */
  onSave?: (hiddenSections: CharacteristicsSection[]) => Promise<void>;
  /** Qual-quant insights for this segment */
  segmentInsight?: SegmentInsight | null;
  /** Segment ID for tracking */
  segmentId?: string;
}

export default function SegmentMarketViewPanel({
  data,
  segmentName,
  hiddenSections = [],
  editMode,
  onSave,
  segmentInsight,
  segmentId,
}: SegmentMarketViewPanelProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftHidden, setDraftHidden] = useState<Set<CharacteristicsSection>>(new Set(hiddenSections));

  if (!data) return null;

  const hasContent = data.market_kpis || data.hcp_characteristics || data.treatment_trends;
  if (!hasContent) return null;

  const handleEdit = () => {
    setDraftHidden(new Set(hiddenSections));
    setEditing(true);
  };

  const handleCancel = () => {
    setDraftHidden(new Set(hiddenSections));
    setEditing(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(Array.from(draftHidden));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section: CharacteristicsSection) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // In view mode, filter out hidden sections. In edit mode, show everything.
  const effectiveHidden = editing ? draftHidden : new Set(hiddenSections);

  return (
    <Root>
      <Header onClick={() => !editing && setExpanded(!expanded)}>
        <span>Segment Characteristics</span>
        <HeaderActions>
          {onSave && editMode && !editing && (
            <IconBtn
              onClick={(e) => { e.stopPropagation(); handleEdit(); }}
              title="Edit visible sections"
            >
              <EditIcon fontSize="small" />
            </IconBtn>
          )}
          {editing && (
            <>
              <SaveBtn
                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                disabled={saving}
                title="Save changes"
              >
                <CheckIcon fontSize="small" />
              </SaveBtn>
              <IconBtn
                onClick={(e) => { e.stopPropagation(); handleCancel(); }}
                disabled={saving}
                title="Cancel"
                style={{ color: '#991b1b' }}
              >
                <CloseIcon fontSize="small" />
              </IconBtn>
            </>
          )}
          {!editing && (
            <ExpandIcon $expanded={expanded}><ExpandMoreIcon /></ExpandIcon>
          )}
        </HeaderActions>
      </Header>
      {expanded && (
        <Content>
          {editing && (
            <SectionToggles>
              {(Object.keys(SECTION_LABELS) as CharacteristicsSection[]).map((key) => {
                const isHidden = draftHidden.has(key);
                return (
                  <SectionToggle
                    key={key}
                    onClick={() => toggleSection(key)}
                    $hidden={isHidden}
                  >
                    {isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    <span>{SECTION_LABELS[key]}</span>
                  </SectionToggle>
                );
              })}
            </SectionToggles>
          )}
          <MarketView
            marketKpis={effectiveHidden.has('kpis') ? undefined : (data.market_kpis as MarketViewProps['marketKpis'])}
            hcpCharacteristics={effectiveHidden.has('hcp_characteristics') ? undefined : (data.hcp_characteristics as MarketViewProps['hcpCharacteristics'])}
            treatmentTrends={effectiveHidden.has('treatment_trends') ? undefined : (data.treatment_trends as MarketViewProps['treatmentTrends'])}
          />
          {segmentInsight && (
            <QualInsightsWrapper>
              <QualQuantOverlay insight={segmentInsight} segmentName={segmentName} defaultOpen={false} />
            </QualInsightsWrapper>
          )}
          <SourceBadge>AI-Generated · Segment Insights</SourceBadge>
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  color: rgba(0, 0, 0, 0.54);
  transition: background 120ms ease;
  &:hover:not(:disabled) { background: rgba(0, 0, 0, 0.06); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const SaveBtn = styled(IconBtn)`
  color: #2d7a2e;
  &:hover:not(:disabled) { background: #e3f9e8; }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const SectionToggles = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const SectionToggle = styled.button<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${p => p.$hidden ? '#ddd' : '#1976d2'};
  background: ${p => p.$hidden ? '#f5f5f5' : '#e3f2fd'};
  color: ${p => p.$hidden ? '#999' : '#1976d2'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 120ms ease;
  &:hover { opacity: 0.85; }
`;

const SourceBadge = styled.div`
  font-size: 11px;
  color: #5C73AD;
  background-color: #f1f0f6;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  font-weight: 600;
`;

const QualInsightsWrapper = styled.div`
  margin-top: 24px;
`;
